import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { InlineLoading, LoadingButton } from "./Spinner";
import CodeViewer, { detectLangFromContentType } from "./CodeViewer";
import VarText from "./VarText";

interface Props {
  requestId: string;
  // The revision to focus on, plus the immediately-previous revision
  // (for the diff). When the focused revision is the very first one,
  // prev is null.
  current: { id: string; revision_no: number } | null;
  prev: { id: string; revision_no: number } | null;
  onClose: () => void;
  onRestored: () => void;
}

// Portaled modal that shows the full snapshot of a request revision —
// mirrors RunDetailModal's layout and keyboard / backdrop behavior. The
// user can restore from here (which writes a NEW revision so nothing is
// lost). The pluggable RevisionDiff renders below when there's a
// previous version to compare against.
export default function RevisionDetailModal({ requestId, current, prev, onClose, onRestored }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["revision", requestId, current?.id],
    queryFn: () => api.requests.revision(requestId, current!.id),
    enabled: current != null,
  });
  const restore = useMutation({
    mutationFn: () => api.requests.restoreRevision(requestId, current!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", requestId] });
      qc.invalidateQueries({ queryKey: ["revisions", requestId] });
      onRestored();
      onClose();
    },
  });

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current, onClose]);

  if (!current) return null;
  const rev = q.data?.revision ?? null;
  const ct = rev ? findHeader(rev.headers ?? [], "content-type") : null;
  const lang = detectLangFromContentType(ct);

  return createPortal(
    <div
      className="fixed inset-0 z-[110] grid place-items-center p-4"
      style={{ background: "var(--backdrop)" }}
      onClick={onClose}
    >
      <div
        className="card w-full max-w-5xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
          <h2 className="text-sm font-semibold">Revision details</h2>
          <span className="chip bg-surface-3 muted-strong mono">#{current.revision_no}</span>
          {rev && (
            <span className="text-xs muted-strong">
              {new Date(rev.created_at).toLocaleString()}
              {rev.author_name ? ` · ${rev.author_name}` : rev.author_email ? ` · ${rev.author_email}` : ""}
            </span>
          )}
          <LoadingButton
            className="btn ml-auto"
            pending={restore.isPending}
            onClick={() => restore.mutate()}
          >Restore this version</LoadingButton>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
          {!rev && <InlineLoading label="Loading revision…" />}
          {rev && (
            <>
              <section className="space-y-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={"method-tag shrink-0 " + rev.method.toLowerCase()}>{rev.method}</span>
                  <VarText value={rev.url ?? ""} className="text-xs font-mono truncate flex-1 min-w-0" />
                </div>
                {rev.name && <div className="text-sm font-medium">{rev.name}</div>}
                {rev.note && (
                  <div className="text-xs muted">Note: <span className="text-text">{rev.note}</span></div>
                )}
              </section>

              {/* Snapshot: query, headers, body. KV blocks render with
                  VarText so {{vars}} look identical to the URL row. */}
              <KVBlock label="Query" pairs={rev.query ?? []} />
              <KVBlock label="Headers" pairs={rev.headers ?? []} />
              {rev.body && (
                <section className="space-y-1.5 min-w-0">
                  <div className="label">Body <span className="muted-strong">({rev.body_type})</span></div>
                  <CodeViewer value={rev.body} lang={lang} />
                </section>
              )}

              {/* Diff against the prior version. RevisionDiff lives in
                  RequestBuilder.tsx; importing it would be a cycle, so
                  we inline the same component here as a thin wrapper. */}
              {prev && (
                <section className="space-y-1.5 min-w-0 border-t border-border pt-3">
                  <div className="label">Changes from #{prev.revision_no} → #{current.revision_no}</div>
                  <RevDiffInline requestId={requestId} fromId={prev.id} toId={current.id} />
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function KVBlock({ label, pairs }: { label: string; pairs: Array<{ key: string; value: string; enabled?: boolean }> }) {
  if (!pairs.length) return null;
  return (
    <section className="space-y-1.5 min-w-0">
      <div className="label">{label} <span className="muted-strong">({pairs.length})</span></div>
      <table className="w-full text-xs border border-border rounded">
        <tbody>
          {pairs.map((p, i) => (
            <tr key={i} className="border-b border-border last:border-0 align-top">
              <th className="text-left font-mono font-normal text-text-dim p-1.5 pr-3 w-[42%] break-all">{p.key}</th>
              <td className="font-mono text-text p-1.5 break-all">
                <VarText value={p.value} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// Inline copy of the diff component, fetched fresh against the same
// query keys as the inline RevisionsPane usage. Defined here to keep
// RequestBuilder.tsx from owning an import cycle.
function RevDiffInline({ requestId, fromId, toId }: { requestId: string; fromId: string; toId: string }) {
  const fromQ = useQuery({
    queryKey: ["revision", requestId, fromId],
    queryFn: () => api.requests.revision(requestId, fromId),
  });
  const toQ = useQuery({
    queryKey: ["revision", requestId, toId],
    queryFn: () => api.requests.revision(requestId, toId),
  });
  if (fromQ.isLoading || toQ.isLoading) return <InlineLoading label="Loading diff…" />;
  const a = fromQ.data?.revision;
  const b = toQ.data?.revision;
  if (!a || !b) return <p className="text-xs muted italic">Couldn't load one or both revisions.</p>;

  const scalars: Array<{ field: string; from: string; to: string }> = [
    { field: "name",        from: a.name ?? "",        to: b.name ?? "" },
    { field: "method",      from: a.method,            to: b.method },
    { field: "URL",         from: a.url ?? "",         to: b.url ?? "" },
    { field: "body type",   from: a.body_type,         to: b.body_type },
    { field: "description", from: a.description ?? "", to: b.description ?? "" },
  ].filter((s) => s.from !== s.to);

  const headerChanges = diffKVList(a.headers ?? [], b.headers ?? []);
  const queryChanges = diffKVList(a.query ?? [], b.query ?? []);
  const bodyChanged = (a.body ?? "") !== (b.body ?? "");
  const anyChange = scalars.length > 0 || bodyChanged || headerChanges.length > 0 || queryChanges.length > 0;
  if (!anyChange) return <p className="text-xs muted italic">No differences.</p>;

  return (
    <div className="space-y-3">
      {scalars.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {scalars.map((s) => (
            <li key={s.field} className="flex items-baseline gap-2 min-w-0">
              <span className="muted-strong shrink-0 capitalize">{s.field}</span>
              <span className="mono text-[11px] flex-1 min-w-0 break-all">
                <span className="muted line-through">{s.from || "∅"}</span>
                <span className="muted-strong mx-1.5">→</span>
                <span className="text-text">{s.to || "∅"}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {headerChanges.length > 0 && <KVDiffList label="Headers" changes={headerChanges} />}
      {queryChanges.length > 0 && <KVDiffList label="Query" changes={queryChanges} />}
      {bodyChanged && <BodyDiff before={a.body ?? ""} after={b.body ?? ""} />}
    </div>
  );
}

// Render the line-by-line diff of two body strings. Implements
// Hunt–McIlroy via an LCS table — O(n*m), fine for the few-kilobyte
// bodies we ship. Capped at 4000 lines per side so a 1MB JSON dump
// doesn't lock the tab; over the cap we fall back to the old
// scalar-style "+N chars" summary.
function BodyDiff({ before, after }: { before: string; after: string }) {
  const aLines = before.split("\n");
  const bLines = after.split("\n");
  const CAP = 4000;
  const delta = after.length - before.length;
  const sign = delta >= 0 ? "+" : "";
  if (aLines.length > CAP || bLines.length > CAP) {
    return (
      <div className="text-xs">
        <div className="muted-strong mb-1">Body</div>
        <div className="muted">Diff too large to inline ({aLines.length} → {bLines.length} lines). Body changed {sign}{delta} chars.</div>
      </div>
    );
  }
  const lines = lineDiff(aLines, bLines);
  return (
    <div className="text-xs min-w-0">
      <div className="muted-strong mb-1 flex items-center gap-2">
        <span>Body</span>
        <span className="text-[10px] muted">{sign}{delta} chars</span>
      </div>
      <pre className="mono text-[11px] leading-[1.45] bg-bg-2 border border-border rounded p-0 max-h-[420px] overflow-auto whitespace-pre">
{lines.map((l, i) => (
  <div
    key={i}
    className={
      "px-2 " + (
        l.kind === "add" ? "bg-good/10 text-text border-l-2 border-good"
        : l.kind === "del" ? "bg-danger/10 text-text border-l-2 border-danger"
        : "text-text-dim border-l-2 border-transparent"
      )
    }
  >
    <span className="select-none inline-block w-3 muted-strong">{l.kind === "add" ? "+" : l.kind === "del" ? "-" : " "}</span>
    {l.line || " "}
  </div>
))}
      </pre>
    </div>
  );
}

// Hunt–McIlroy line diff. Returns an array of segments tagged with
// ctx / add / del. We compute the LCS bottom-up then walk it to emit
// the edit script. Strings are split on newlines by the caller.
type DiffSeg = { kind: "ctx" | "add" | "del"; line: string };
function lineDiff(A: string[], B: string[]): DiffSeg[] {
  const n = A.length, m = B.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffSeg[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ kind: "ctx", line: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ kind: "del", line: A[i] }); i++; }
    else { out.push({ kind: "add", line: B[j] }); j++; }
  }
  while (i < n) out.push({ kind: "del", line: A[i++] });
  while (j < m) out.push({ kind: "add", line: B[j++] });
  return out;
}

// Treat the KV lists as ordered key→value maps. Same-key + different
// value renders as an inline edit; missing on one side is add/remove.
type KVChange =
  | { kind: "added";   key: string; value: string }
  | { kind: "removed"; key: string; value: string }
  | { kind: "changed"; key: string; from: string; to: string };
function diffKVList(a: Array<{ key: string; value: string }>, b: Array<{ key: string; value: string }>): KVChange[] {
  // Use index-1 of duplicate keys to preserve order; otherwise the
  // "first occurrence" wins (good enough for the typical case).
  const am = new Map<string, string>();
  const bm = new Map<string, string>();
  for (const { key, value } of a) if (!am.has(key)) am.set(key, value);
  for (const { key, value } of b) if (!bm.has(key)) bm.set(key, value);
  const out: KVChange[] = [];
  for (const [k, v] of am) {
    if (!bm.has(k)) out.push({ kind: "removed", key: k, value: v });
    else if (bm.get(k) !== v) out.push({ kind: "changed", key: k, from: v, to: bm.get(k)! });
  }
  for (const [k, v] of bm) if (!am.has(k)) out.push({ kind: "added", key: k, value: v });
  return out;
}

function KVDiffList({ label, changes }: { label: string; changes: KVChange[] }) {
  return (
    <div className="text-xs min-w-0">
      <div className="muted-strong mb-1">{label} <span className="muted">({changes.length})</span></div>
      <ul className="space-y-0.5">
        {changes.map((c, i) => (
          <li key={i} className="font-mono text-[11px] break-all">
            {c.kind === "added" && (
              <span className="text-good">+ {c.key}: {c.value}</span>
            )}
            {c.kind === "removed" && (
              <span className="text-danger">- {c.key}: {c.value}</span>
            )}
            {c.kind === "changed" && (
              <>
                <span className="text-danger block">- {c.key}: {c.from}</span>
                <span className="text-good block">+ {c.key}: {c.to}</span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function findHeader(pairs: Array<{ key: string; value: string }>, name: string): string | null {
  const m = pairs.find((p) => p.key.toLowerCase() === name.toLowerCase());
  return m ? m.value : null;
}
